import { BadgeCheck, Building2, HardHat, KeyRound, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";

// Статус аккаунта — Собственник / Риелтор / Агентство / Застройщик — одним
// видом везде: карточки, страница объявления, профили, кабинет, чат.
// Цвет закреплён за типом, чтобы его узнавали с первого взгляда.

const ACCOUNT_TYPES = {
  USER: { Icon: KeyRound, dot: "bg-emerald-500", pill: "bg-emerald-50 text-emerald-800 ring-emerald-200/70", fallback: "Собственник" },
  REALTOR: { Icon: UserRound, dot: "bg-sky-500", pill: "bg-sky-50 text-sky-800 ring-sky-200/70", fallback: "Риелтор" },
  AGENCY: { Icon: Building2, dot: "bg-violet-500", pill: "bg-violet-50 text-violet-800 ring-violet-200/70", fallback: "Агентство" },
  COMPANY: { Icon: HardHat, dot: "bg-amber-500", pill: "bg-amber-50 text-amber-800 ring-amber-200/70", fallback: "Застройщик" },
} as const;

export type AccountType = keyof typeof ACCOUNT_TYPES;

/** «OWNER» в части API и «USER» в другой — один и тот же собственник. */
export function normalizeAccountType(raw: unknown): AccountType | null {
  const key = String(raw ?? "").toUpperCase();
  if (key === "OWNER" || key === "PRIVATE") return "USER";
  return key in ACCOUNT_TYPES ? (key as AccountType) : null;
}

export function useAccountTypeLabel() {
  const { t } = useTranslation();
  return (type: AccountType) => t(`ownerBadge.${type}`, { defaultValue: ACCOUNT_TYPES[type].fallback });
}

const SIZES = {
  xs: { pill: "h-5 gap-1 pl-0.5 pr-1.5 text-[10.5px]", dot: "h-4 w-4", icon: "h-2.5 w-2.5", tick: "h-3 w-3" },
  sm: { pill: "h-6 gap-1.5 pl-0.5 pr-2 text-[11.5px]", dot: "h-5 w-5", icon: "h-3 w-3", tick: "h-3.5 w-3.5" },
  md: { pill: "h-7 gap-1.5 pl-1 pr-2.5 text-[12.5px]", dot: "h-5 w-5", icon: "h-3 w-3", tick: "h-4 w-4" },
  lg: { pill: "h-8 gap-2 pl-1 pr-3 text-[13.5px]", dot: "h-6 w-6", icon: "h-3.5 w-3.5", tick: "h-[18px] w-[18px]" },
  xl: { pill: "h-8 gap-1.5 pl-1 pr-3 text-[12.5px] sm:h-9 sm:gap-2 sm:pr-3.5 sm:text-[15px]", dot: "h-6 w-6 sm:h-7 sm:w-7", icon: "h-3.5 w-3.5 sm:h-4 sm:w-4", tick: "h-5 w-5" },
} as const;

/**
 * Пилюля: цветной кружок с иконкой типа + название; у проверенного аккаунта
 * в конце синяя галочка (и «· Проверен» при `showVerifiedText`).
 */
export function AccountTypeBadge({
  type,
  verified = false,
  size = "sm",
  showVerifiedText = false,
  className = "",
}: {
  type: unknown;
  verified?: boolean;
  size?: keyof typeof SIZES;
  showVerifiedText?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const label = useAccountTypeLabel();
  const kind = normalizeAccountType(type);
  if (!kind) return null;
  const { Icon, dot, pill } = ACCOUNT_TYPES[kind];
  const s = SIZES[size];
  const text = label(kind);
  const verifiedText = t("ownerBadge.verified", { defaultValue: "Проверен" });
  return (
    <span
      title={verified ? `${text} · ${verifiedText}` : text}
      className={`inline-flex min-w-0 max-w-full shrink items-center rounded-full font-bold leading-none ring-1 ring-inset ${pill} ${s.pill} ${className}`}
    >
      <span className={`flex shrink-0 items-center justify-center rounded-full text-white ${dot} ${s.dot}`} aria-hidden="true">
        <Icon className={s.icon} strokeWidth={2.5} />
      </span>
      <span className="truncate">{text}</span>
      {verified && (
        <>
          {showVerifiedText && <span className="shrink-0 font-semibold opacity-70">· {verifiedText}</span>}
          <BadgeCheck className={`shrink-0 fill-blue-500 text-white ${s.tick}`} strokeWidth={2.25} aria-label={verifiedText} />
        </>
      )}
    </span>
  );
}
